import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/App";
import { EASE } from "@/components/Reveal";

const lines = ["WE ANSWER.", "WE PROCESS.", "YOU SCALE."];

const HeroImage =
  "https://images.pexels.com/photos/946310/pexels-photo-946310.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const MaskedLine = ({ text, index }) => (
  <div className="overflow-hidden">
    <motion.span
      className="block whitespace-nowrap font-display text-5xl font-black leading-[0.95] tracking-tighter text-white sm:text-7xl lg:text-[5.5rem]"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.35 + index * 0.14, ease: EASE }}
    >
      {index === 2 ? <span className="text-sky-400">{text}</span> : text}
    </motion.span>
  </div>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-36 pb-24 md:pt-44 md:pb-32">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 right-[-10%] h-[520px] w-[520px] rounded-full bg-sky-500/15 blur-[140px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-[-20%] left-[-10%] h-[420px] w-[420px] rounded-full bg-blue-800/20 blur-[120px]"
      />

      <div className="relative mx-auto grid max-w-7xl gap-16 px-6 md:px-12 lg:grid-cols-[1.25fr_1fr] lg:gap-10">
        <div>
          <motion.p
            data-testid="hero-overline"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-sky-400 md:text-sm"
          >
            <span className="h-px w-10 bg-sky-400" />
            Global BPO Partner — Est. for Ambitious Brands
          </motion.p>

          <h1 data-testid="hero-heading" className="space-y-1">
            {lines.map((l, i) => (
              <MaskedLine key={l} text={l} index={i} />
            ))}
          </h1>

          <motion.p
            data-testid="hero-subheading"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-10 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg"
          >
            Future Solutions is your outsourced operations team — 24/7 customer support across voice,
            chat and email, plus back-office and data processing that runs like clockwork.
            You focus on growth. We handle everything behind it.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              onClick={scrollToSection("#contact")}
              data-testid="hero-cta-quote"
              className="group inline-flex items-center gap-2 rounded-full bg-sky-400 px-7 py-3.5 text-sm font-bold text-[#0A0F1C] transition-colors duration-300 hover:bg-white"
            >
              Get a Quote
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#services"
              onClick={scrollToSection("#services")}
              data-testid="hero-cta-services"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:border-sky-400 hover:text-sky-400"
            >
              Explore Services
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.1, delay: 0.8, ease: EASE }}
          className="relative hidden lg:block"
        >
          <div className="relative h-[520px] overflow-hidden border border-white/10">
            <motion.img
              src={HeroImage}
              alt="Modern corporate operations floor"
              data-testid="hero-image"
              style={{ y: imgY, scale: imgScale }}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C] via-[#0A0F1C]/30 to-transparent" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
            className="absolute -bottom-8 -left-10 border border-white/10 bg-[#111827]/90 px-6 py-5 backdrop-blur-md"
            data-testid="hero-stat-chip"
          >
            <p className="font-display text-3xl font-black text-sky-400">24/7</p>
            <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-400">Always-on coverage</p>
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={scrollToSection("#services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 left-6 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-slate-500 transition-colors duration-300 hover:text-sky-400 md:left-12"
        aria-label="Scroll to services"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown className="h-4 w-4" />
        </motion.span>
        Scroll
      </motion.button>
    </section>
  );
};

export default Hero;
